import { useCallback, useState } from "react";
import { Modal, Spin, message } from "antd";
import PdfViewerModal from "components/PrintShippingLabel/PdfViewerModal";
import {
  markFactoryParcelPrint,
  printFactoryParcel,
} from "services/factoryParcelApi";
import { extractParcelLabelUrl } from "./parcelLabelPrint";

export type OpenParcelLabelPreviewInput = {
  parcel_id: string;
  tracking_number: string;
  carrier_code: string;
  /** URL จาก create/list ถ้า API print ไม่คืน link */
  label_url?: string | null;
  title?: string;
};

type UseParcelLabelPreviewOptions = {
  /** เรียกหลังมาร์ก is_print สำเร็จ — ใช้ reload ตาราง */
  onMarkedPrint?: () => void;
};

/**
 * เปิด Modal ตัวอย่าง Label ก่อนพิมพ์ — รูปแบบเดียวกับ TrackingNumberPanel / PrintShippingLabel
 */
export function useParcelLabelPreview(options?: UseParcelLabelPreviewOptions) {
  const { onMarkedPrint } = options ?? {};
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState("");
  const [fetching, setFetching] = useState(false);
  const [pendingParcelId, setPendingParcelId] = useState<string | null>(null);

  const closePreview = useCallback(() => {
    if (pdfUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfOpen(false);
    setPdfUrl(null);
    setPdfTitle("");
    setPendingParcelId(null);
    setFetching(false);
  }, [pdfUrl]);

  const handleAfterPrint = useCallback(() => {
    if (!pendingParcelId) return;
    markFactoryParcelPrint({ parcel_ids: [pendingParcelId] })
      .then(() => {
        onMarkedPrint?.();
      })
      .catch(() => {
        message.warning("ส่งพิมพ์แล้ว แต่มาร์กสถานะพิมพ์ไม่สำเร็จ");
      });
  }, [pendingParcelId, onMarkedPrint]);

  const openPreview = useCallback(async (input: OpenParcelLabelPreviewInput) => {
    if (!input.tracking_number?.trim()) {
      message.warning("ไม่มีเลขพัสดุ");
      return;
    }
    if (!input.parcel_id?.trim()) {
      message.warning("ไม่พบรหัสรายการพัสดุ");
      return;
    }
    setPendingParcelId(input.parcel_id);
    setPdfTitle(input.title ?? input.tracking_number);

    // ถ้ามี URL อยู่แล้ว ใช้ทันทีโดยไม่ต้องเรียก API
    const existingUrl = input.label_url?.trim();
    if (existingUrl) {
      setPdfUrl(existingUrl);
      setPdfOpen(true);
      return;
    }

    // ไม่มี URL — เปิด modal พร้อม spinner แล้วเรียก API
    setPdfUrl(null);
    setFetching(true);
    setPdfOpen(true);
    try {
      const res = await printFactoryParcel({
        tracking_number: input.tracking_number,
        carrier_code: input.carrier_code,
      });
      const url = extractParcelLabelUrl(res);
      if (!url) {
        message.error("ไม่พบไฟล์ Label");
        setPdfOpen(false);
        return;
      }
      setPdfUrl(url);
    } catch {
      message.error("โหลด Label ไม่สำเร็จ");
      setPdfOpen(false);
    } finally {
      setFetching(false);
    }
  }, []);

  const previewModal = (
    <Modal
      title={`Label — ${pdfTitle}`}
      open={pdfOpen}
      onCancel={closePreview}
      footer={null}
      width={560}
      style={{ top: 24 }}
      destroyOnClose
      transitionName=""
      maskTransitionName=""
    >
      {fetching ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <Spin size="large" tip="กำลังโหลด Label..." />
        </div>
      ) : (
        <PdfViewerModal
          pdfUrl={pdfUrl}
          title={pdfTitle}
          onAfterPrint={handleAfterPrint}
        />
      )}
    </Modal>
  );

  return { openPreview, closePreview, previewModal };
}
